import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiRequest } from "@core/services/api/apiRequest";
import { getOnboardingPathFromInvitation } from "@core/features/onboarding";
import type { ClaimedVia } from "../invitation.types";

export type ClaimInvitationArgs = {
  shortcode: string;
  claimedVia?: ClaimedVia;
};

export type ClaimInvitationResponse = {
  program_id: number;
  invitation_record_id: number;
  invitation_type: string;
  organization_id: number | null;
};

/**
 * Hook for existing users to claim an invitation.
 *
 * After claiming, always redirects to `/dashboard/:programId`.
 * The OnboardingGate component will redirect to onboarding if needed.
 *
 * @example
 * ```tsx
 * const { mutate: claimInvitation, isPending } = useClaimInvitation();
 *
 * const handleJoin = () => {
 *   claimInvitation({
 *     shortcode: invitation.shortcode,
 *     claimedVia: 'direct_link',
 *   });
 * };
 * ```
 */
export function useClaimInvitation() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      shortcode,
      claimedVia = "direct_link",
    }: ClaimInvitationArgs): Promise<ClaimInvitationResponse> => {
      return apiRequest(`/invitation/${shortcode}/claim/`, "POST", {
        claimed_via: claimedVia,
      });
    },
    onSuccess: async (data) => {
      // Await invalidation so the OnboardingGate has fresh data when it mounts
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["actions"] }),
        queryClient.invalidateQueries({ queryKey: ["programs"] }),
      ]);

      navigate(
        getOnboardingPathFromInvitation({
          invitationType: data.invitation_type,
          organizationId: data.organization_id,
          programId: data.program_id,
        })
      );
    },
  });
}
